import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { followsAPI, postsAPI, sellersAPI, accessAPI, companiesAPI } from '../../services/api';
import {
  Building2,
  FileText,
  Heart,
  Loader2,
  MapPin,
  Calendar,
  ChevronRight,
  Bell,
  Search,
  TrendingUp,
  Target,
  Clock,
  Star,
  Filter,
  BarChart3,
  Briefcase,
  Globe,
  Zap,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  ArrowUpRight,
  Sparkles,
  Activity,
  ListFilter,
  LayoutGrid,
  List,
  X,
  Unlock,
  Key,
  Users,
  MessageSquare,
  UserCheck,
} from 'lucide-react';

// Post type configuration matching backend POST_TYPES
const POST_TYPE_CONFIG = {
  pipeline_milestone: { label: 'Pipeline Milestone', color: 'bg-purple-100 text-purple-700', icon: TrendingUp },
  partnership_news: { label: 'Partnership', color: 'bg-blue-100 text-blue-700', icon: Briefcase },
  fundraising: { label: 'Fundraising', color: 'bg-green-100 text-green-700', icon: Zap },
  clinical_data: { label: 'Clinical Data', color: 'bg-teal-100 text-teal-700', icon: Activity },
  regulatory: { label: 'Regulatory', color: 'bg-orange-100 text-orange-700', icon: FileText },
  team_update: { label: 'Team Update', color: 'bg-indigo-100 text-indigo-700', icon: Building2 },
  conference: { label: 'Conference', color: 'bg-pink-100 text-pink-700', icon: Calendar },
  general: { label: 'General', color: 'bg-gray-100 text-gray-600', icon: FileText },
};

// Get display label for post type
const getPostTypeLabel = (postType) => {
  return POST_TYPE_CONFIG[postType]?.label || postType;
};

// Get color class for post type
const getPostTypeColor = (postType) => {
  return POST_TYPE_CONFIG[postType]?.color || 'bg-gray-100 text-gray-600';
};

// Priority badge component
const PriorityBadge = ({ priority }) => {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${styles[priority] || styles.low}`}>
      {priority?.toUpperCase()}
    </span>
  );
};

// Stage indicator component
const StageIndicator = ({ stage }) => {
  const stageColors = {
    'Discovery': 'bg-blue-500',
    'Preclinical': 'bg-indigo-500',
    'Phase 1': 'bg-purple-500',
    'Phase 2': 'bg-pink-500',
    'Phase 3': 'bg-orange-500',
    'Approved': 'bg-green-500',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full text-white ${stageColors[stage] || 'bg-gray-500'}`}>
      <span className="w-1.5 h-1.5 bg-white/50 rounded-full"></span>
      {stage}
    </span>
  );
};

// Access level labels
const ACCESS_LEVEL_LABELS = {
  interested: 'Interested Buyer',
  deep_dive: 'Deep Dive',
  deal_terms: 'Full Deal Terms',
};

export default function BuyerDashboard() {
  const { userProfile } = useAuth();
  const [followedSellers, setFollowedSellers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [feedFilter, setFeedFilter] = useState('all'); // all, milestones, partnerships, news
  const [watchlistView, setWatchlistView] = useState('compact'); // compact, detailed
  const [recentApprovals, setRecentApprovals] = useState([]);
  const [colleagueActivity, setColleagueActivity] = useState([]);
  const [company, setCompany] = useState(null);

  const buyerName = userProfile?.name || userProfile?.buyers?.company_name || 'BD Professional';
  const companyName = userProfile?.buyers?.company_name || '';

  useEffect(() => {
    loadFollowedSellers();
    loadFeed();
    loadRecentApprovals();
    loadColleagueActivity();
  }, []);

  const loadFollowedSellers = async () => {
    setSellersLoading(true);
    try {
      const response = await followsAPI.getFollowed();
      setFollowedSellers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load followed sellers:', error);
    } finally {
      setSellersLoading(false);
    }
  };

  const loadFeed = async () => {
    setPostsLoading(true);
    try {
      const response = await postsAPI.getBuyerFeed({ limit: 50 });
      setPosts(response.data?.data?.posts || []);
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const loadRecentApprovals = async () => {
    try {
      const response = await accessAPI.getRecentApprovals();
      setRecentApprovals(response.data?.data || []);
    } catch (error) {
      console.debug('Failed to load recent approvals:', error);
    }
  };

  const loadColleagueActivity = async () => {
    try {
      // First get user's company
      const companyRes = await companiesAPI.getMyCompany();
      if (companyRes.data?.data) {
        const companyData = companyRes.data.data;
        setCompany(companyData);
        // Then load colleague activity
        const activityRes = await companiesAPI.getColleagueActivity(companyData.id, { limit: 10 });
        if (activityRes.data?.data) {
          setColleagueActivity(activityRes.data.data);
        }
      }
    } catch (error) {
      console.debug('Failed to load colleague activity:', error);
    }
  };

  const dismissApproval = async (requestId) => {
    try {
      await accessAPI.acknowledgeApproval(requestId);
      setRecentApprovals((prev) => prev.filter((a) => a.id !== requestId));
    } catch (error) {
      console.error('Failed to dismiss approval:', error);
    }
  };

  const handleUnfollow = async (sellerId) => {
    try {
      await followsAPI.unfollow(sellerId);
      setFollowedSellers((prev) => prev.filter((s) => s.seller_id !== sellerId));
    } catch (error) {
      console.error('Failed to unfollow:', error);
    }
  };

  // Computed metrics
  const metrics = useMemo(() => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const todayPosts = posts.filter(p => {
      const postDate = new Date(p.published_at);
      return postDate.toDateString() === today.toDateString();
    });

    const weekPosts = posts.filter(p => {
      const postDate = new Date(p.published_at);
      return postDate >= weekAgo;
    });

    const milestones = posts.filter(p => p.post_type === 'pipeline_milestone' || p.post_type === 'clinical_data');
    const partnerships = posts.filter(p => p.post_type === 'partnership_news');
    const exclusiveContent = posts.filter(p => p.visibility === 'buyers_only');

    return {
      watchlistCount: followedSellers.length,
      todayUpdates: todayPosts.length,
      weekUpdates: weekPosts.length,
      milestones: milestones.length,
      partnerships: partnerships.length,
      exclusiveAccess: exclusiveContent.length,
    };
  }, [posts, followedSellers]);

  // Filter posts based on selected filter
  const filteredPosts = useMemo(() => {
    if (feedFilter === 'all') return posts;
    if (feedFilter === 'milestones') return posts.filter(p =>
      p.post_type === 'pipeline_milestone' || p.post_type === 'clinical_data'
    );
    if (feedFilter === 'partnerships') return posts.filter(p => p.post_type === 'partnership_news');
    if (feedFilter === 'news') return posts.filter(p =>
      p.post_type === 'regulatory' || p.post_type === 'fundraising' ||
      p.post_type === 'team_update' || p.post_type === 'conference' || p.post_type === 'general'
    );
    if (feedFilter === 'exclusive') return posts.filter(p => p.visibility === 'buyers_only');
    return posts;
  }, [posts, feedFilter]);

  // Group watchlist by stage
  const watchlistByStage = useMemo(() => {
    const groups = {};
    followedSellers.forEach(seller => {
      const stage = seller.stage || 'Unknown';
      if (!groups[stage]) groups[stage] = [];
      groups[stage].push(seller);
    });
    return groups;
  }, [followedSellers]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            BD Intelligence Dashboard
          </h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Last updated: {new Date().toLocaleString()}
            {companyName && (
              <>
                <span className="text-gray-300">|</span>
                <Building2 className="w-4 h-4" />
                {companyName}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/buyer/search"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Search className="w-4 h-4" />
            Search Pipeline
          </Link>
        </div>
      </div>

      {/* Access Approval Notifications */}
      {recentApprovals.length > 0 && (
        <div className="space-y-2">
          {recentApprovals.map((approval) => (
            <div
              key={approval.id}
              className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center gap-4 shadow-sm"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Unlock className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-green-800">
                  Access Granted: {approval.company_name}
                </p>
                <p className="text-sm text-green-600">
                  You now have <span className="font-semibold">{ACCESS_LEVEL_LABELS[approval.access_level] || approval.access_level}</span> access.
                  {approval.response_message && (
                    <span className="ml-1 text-green-500">"{approval.response_message}"</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to={`/buyer/company/${approval.seller_id}`}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  View Profile
                </Link>
                <button
                  onClick={() => dismissApproval(approval.id)}
                  className="p-1.5 text-green-400 hover:text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                  title="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Executive Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Eye className="w-5 h-5 text-blue-600" />
            <span className="text-xs text-green-600 font-medium">Active</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{metrics.watchlistCount}</div>
          <div className="text-xs text-gray-500">Watchlist</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Zap className="w-5 h-5 text-amber-600" />
            {metrics.todayUpdates > 0 && (
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            )}
          </div>
          <div className="text-2xl font-bold text-gray-900">{metrics.todayUpdates}</div>
          <div className="text-xs text-gray-500">Today's Updates</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{metrics.weekUpdates}</div>
          <div className="text-xs text-gray-500">This Week</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{metrics.milestones}</div>
          <div className="text-xs text-gray-500">Milestones</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Briefcase className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{metrics.partnerships}</div>
          <div className="text-xs text-gray-500">Partnerships</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Star className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{metrics.exclusiveAccess}</div>
          <div className="text-xs text-gray-500">Exclusive Intel</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Feed Section */}
        <div className="lg:col-span-2 space-y-4">
          {/* Feed Header with Filters */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  Market Intelligence Feed
                </h2>
                <div className="flex items-center gap-2">
                  <ListFilter className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">{filteredPosts.length} updates</span>
                </div>
              </div>

              {/* Filter Pills */}
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { key: 'all', label: 'All Updates', icon: LayoutGrid },
                  { key: 'milestones', label: 'Milestones', icon: TrendingUp },
                  { key: 'partnerships', label: 'Partnerships', icon: Briefcase },
                  { key: 'news', label: 'News', icon: FileText },
                  { key: 'exclusive', label: 'Exclusive', icon: Star },
                ].map(filter => (
                  <button
                    key={filter.key}
                    onClick={() => setFeedFilter(filter.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      feedFilter === filter.key
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <filter.icon className="w-3.5 h-3.5" />
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Feed Content */}
            {postsLoading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Loading market intelligence...</p>
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {filteredPosts.slice(0, 20).map((post) => (
                  <Link
                    key={post.id}
                    to={`/post/${post.id}`}
                    className="block p-4 hover:bg-gray-50 transition-colors group cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      {/* Company Avatar */}
                      <div
                        onClick={(e) => { e.preventDefault(); window.location.href = `/buyer/company/${post.seller_id}`; }}
                        className="flex-shrink-0 cursor-pointer"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                          {post.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span
                            onClick={(e) => { e.preventDefault(); window.location.href = `/buyer/company/${post.seller_id}`; }}
                            className="font-medium text-gray-900 hover:text-blue-600 transition-colors cursor-pointer"
                          >
                            {post.company_name}
                          </span>

                          {/* Type Badge */}
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPostTypeColor(post.post_type)}`}>
                            {getPostTypeLabel(post.post_type)}
                          </span>

                          {/* Exclusive Badge */}
                          {post.visibility === 'buyers_only' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                              <Star className="w-3 h-3" />
                              Exclusive
                            </span>
                          )}

                          {/* Timestamp */}
                          <span className="text-xs text-gray-400 ml-auto">
                            {post.published_at && new Date(post.published_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                          {post.title}
                        </h3>

                        {/* Excerpt */}
                        {(post.excerpt || post.content) && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {post.excerpt || post.content?.substring(0, 200)}
                          </p>
                        )}

                        {/* Meta Row */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          {post.headquarters && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {post.headquarters}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Read Full Post
                            <ArrowUpRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No updates match your filter</p>
                <p className="text-sm text-gray-400 mb-4">
                  {followedSellers.length === 0
                    ? 'Add companies to your watchlist to receive updates'
                    : 'Try adjusting your filter or check back later'
                  }
                </p>
                <Link
                  to="/buyer/search"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Search className="w-4 h-4" />
                  Search Companies
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Watchlist */}
        <div className="space-y-4">
          {/* Watchlist Panel */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  Watchlist
                </h2>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setWatchlistView('compact')}
                    className={`p-1.5 rounded ${watchlistView === 'compact' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setWatchlistView('detailed')}
                    className={`p-1.5 rounded ${watchlistView === 'detailed' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {followedSellers.length} companies tracked
              </p>
            </div>

            {sellersLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading watchlist...</p>
              </div>
            ) : followedSellers.length > 0 ? (
              <div className="max-h-[400px] overflow-y-auto">
                {watchlistView === 'compact' ? (
                  // Compact List View
                  <div className="divide-y divide-gray-100">
                    {followedSellers.map((follow) => (
                      <div
                        key={follow.seller_id}
                        className="p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors group"
                      >
                        <Link
                          to={`/buyer/company/${follow.seller_id}`}
                          className="flex-shrink-0"
                        >
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-xs">
                            {follow.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                          </div>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/buyer/company/${follow.seller_id}`}
                            className="font-medium text-gray-900 hover:text-blue-600 block truncate text-sm"
                          >
                            {follow.company_name || `Company #${follow.seller_id}`}
                          </Link>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {follow.stage && <StageIndicator stage={follow.stage} />}
                            {follow.headquarters && (
                              <span className="truncate">{follow.headquarters}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnfollow(follow.seller_id)}
                          className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove from watchlist"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Detailed Grid View
                  <div className="p-3 grid gap-3">
                    {followedSellers.map((follow) => (
                      <Link
                        key={follow.seller_id}
                        to={`/buyer/company/${follow.seller_id}`}
                        className="block p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-colors group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            {follow.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 group-hover:text-blue-600 truncate">
                              {follow.company_name || `Company #${follow.seller_id}`}
                            </div>
                            {follow.stage && (
                              <div className="mt-1">
                                <StageIndicator stage={follow.stage} />
                              </div>
                            )}
                            {follow.headquarters && (
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {follow.headquarters}
                              </div>
                            )}
                            {follow.therapeutic_areas?.length > 0 && (
                              <div className="text-xs text-gray-500 mt-1 truncate">
                                {follow.therapeutic_areas.slice(0, 2).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Eye className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">No companies in watchlist</p>
                <Link
                  to="/buyer/search"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Browse companies →
                </Link>
              </div>
            )}

            {followedSellers.length > 0 && (
              <div className="p-3 border-t border-gray-100">
                <Link
                  to="/buyer/search"
                  className="flex items-center justify-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Add more companies
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Quick Actions
            </h3>
            <div className="space-y-2">
              <Link
                to="/buyer/matches"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 group-hover:text-amber-600">
                    Smart Matches
                  </div>
                  <div className="text-xs text-gray-500">AI-matched sellers</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>

              <Link
                to="/buyer/search"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Search className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                    Search Companies
                  </div>
                  <div className="text-xs text-gray-500">Find new opportunities</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>

              <Link
                to="/buyer/messages"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                    Messages
                  </div>
                  <div className="text-xs text-gray-500">View conversations</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>
            </div>
          </div>

          {/* Activity Summary */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-4 text-white">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Weekly Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-blue-100">New Opportunities</span>
                <span className="font-semibold">{metrics.weekUpdates}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-100">Clinical Milestones</span>
                <span className="font-semibold">{metrics.milestones}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-100">Partnership Signals</span>
                <span className="font-semibold">{metrics.partnerships}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-100">Exclusive Updates</span>
                <span className="font-semibold">{metrics.exclusiveAccess}</span>
              </div>
            </div>
          </div>

          {/* Colleague Activity */}
          {company && colleagueActivity.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-600" />
                  Team Activity
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Recent activity from {company.name} colleagues
                </p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[250px] overflow-y-auto">
                {colleagueActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="p-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.activity_type === 'follow' ? 'bg-blue-100' :
                        activity.activity_type === 'message' ? 'bg-purple-100' :
                        activity.activity_type === 'request_access' ? 'bg-amber-100' :
                        'bg-gray-100'
                      }`}>
                        {activity.activity_type === 'follow' && <UserCheck className="w-4 h-4 text-blue-600" />}
                        {activity.activity_type === 'message' && <MessageSquare className="w-4 h-4 text-purple-600" />}
                        {activity.activity_type === 'request_access' && <Key className="w-4 h-4 text-amber-600" />}
                        {!['follow', 'message', 'request_access'].includes(activity.activity_type) && (
                          <Activity className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">
                            {activity.preferred_name || activity.first_name || 'A colleague'}
                          </span>
                          {activity.activity_type === 'follow' && ' started following '}
                          {activity.activity_type === 'message' && ' messaged '}
                          {activity.activity_type === 'request_access' && ' requested access to '}
                          {activity.activity_type === 'view_profile' && ' viewed '}
                          <Link
                            to={`/buyer/company/${activity.seller_id}`}
                            className="font-medium text-blue-600 hover:text-blue-700"
                          >
                            {activity.target_company_name || 'a company'}
                          </Link>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(activity.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-gray-100">
                <Link
                  to="/buyer/organization"
                  className="flex items-center justify-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  View all team activity
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
